import { useEffect, useState, type CSSProperties } from 'react';
import { Objectives } from './Objectives';
import { md } from '../../lib/md';
import { newlyPassed, verdict } from '../../lib/grade';
import { MEASURED_AGAINST_A_MODEL } from '../../lib/checkpoint';
import { utcDate } from '../../lib/format';
import { usesGit } from '../../lib/uses-git';
import { useLocale, useStrings } from '../../state/useLocale';
import type { GradeResult, PublicMission } from '../../types';

/**
 * The ticket, and what has to become true.
 *
 * There is no header here. The level number, its title and its world are
 * already in the bar directly above, and printing them twice was the single
 * largest piece of repetition on the screen. The rules sit inside the
 * objectives panel because they are a footnote to the same question, not a
 * separate one.
 *
 * The verdict sits directly under the objectives and waits for them: the
 * marks that flipped in a check snap in 140ms apart, and the verdict wipes in
 * on the beat after the last one. Both are timed by the same batch, so this
 * component, which sees each grade arrive, works out once which marks are
 * resolving and hands the ids to the list and the count to the verdict.
 */

export interface MissionBriefProps {
  mission: PublicMission;
  grade: GradeResult | null;
}

interface Seen {
  grade: GradeResult | null;
  /** Visible objectives that flipped to passed when `grade` arrived, in list order. */
  resolving: string[];
}

const NONE: string[] = [];

export function MissionBrief({ mission, grade }: MissionBriefProps) {
  const s = useStrings().play.brief;
  const { locale } = useLocale();
  const measured = MEASURED_AGAINST_A_MODEL.get(mission.id);

  useEffect(() => {
    document.title = s.documentTitle(mission.level, mission.title);
  }, [mission.level, mission.title, s]);

  // The batch is worked out during render, not in an effect, so the first
  // paint of a new grade already carries the right beat numbers: a CSS
  // animation reads its delay when it starts, and it starts on that frame.
  // A grade present at mount is a restored session, and nothing resolves
  // then; the list's mount guard keeps its sounds quiet for the same reason.
  const [seen, setSeen] = useState<Seen>(() => ({ grade, resolving: NONE }));
  if ((seen.grade?.gradedAt ?? null) !== (grade?.gradedAt ?? null)) {
    const ids = newlyPassed(mission, seen.grade, grade);
    setSeen({ grade, resolving: ids.length > 0 ? ids : NONE });
  }
  const { resolving } = seen;

  const said = grade ? verdict(mission, grade) : null;
  // The wipe waits one beat per resolving mark; with none it starts at once.
  const verdictStyle: CSSProperties & { '--n': number } = { '--n': resolving.length };

  return (
    <>
      <div className="prose ticket" dangerouslySetInnerHTML={md(mission.brief)} />
      {/* Only where a model has been measured failing it: inviting AI onto a
          checkpoint a model passes alone would empty the badge. */}
      {measured && (
        <p className="note">
          {s.anyTool(measured.model, measured.modelId, utcDate(new Date(measured.on), locale), measured.runs)}
        </p>
      )}
      {mission.track !== 'checkpoint' && <p className="note">{usesGit(mission) ? s.anyToolLevel : s.anyToolNoRepo}</p>}

      <section className="block">
        <h2 className="label">{s.objectives}</h2>
        <Objectives mission={mission} grade={grade} resolving={resolving} />

        {/* Keyed on the grade so every check wipes it in again, not only the
            first. Failure does not perform; the wipe is the same either way. */}
        {grade && said && (
          <p key={grade.gradedAt} className={`verdict ${grade.passed ? 'is-pass' : 'is-fail'}`} style={verdictStyle}>
            <strong>{said.headline}</strong> <span className="verdict-detail">{said.detail}</span>
          </p>
        )}

        {mission.constraints.length > 0 && (
          <ul className="rules">
            {mission.constraints.map((c, i) => (
              <li key={i} dangerouslySetInnerHTML={md(c)} />
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

export default MissionBrief;
